import { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { QueryField, QueryRule } from '../../../../types/QueryBuilder';
import { useLanguage } from '../../../../context/LanguageContext';
import FieldRow from './SimpleQuery/FieldRow';
import SelectionSummary from './SimpleQuery/SelectionSummary';
import {
    CLIENT_LISTS_CATEGORY_TITLE_KEY,
    GDPR_CATEGORY_TITLE_KEY,
    MERGED_INTO_FIELD_NAMES,
    NOMENCLATURE_FIELD_NAMES,
    PAIRED_FIELD_NAME,
    PURCHASE_BEHAVIOR_CATEGORY_TITLE_KEY,
    groupFieldsBySubcategory,
    toHelpKey,
} from './SimpleQuery/fieldConfig';

interface SimpleQueryGroupProps {
    fields: QueryField[];
    rules: QueryRule[];
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
}

const DEFAULT_GRID_COLUMNS = { xs: '1fr', md: '1fr 1fr' };

const GRID_COLUMNS_BY_CATEGORY: Record<string, string | Record<string, string>> = {
    [GDPR_CATEGORY_TITLE_KEY]: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
    [CLIENT_LISTS_CATEGORY_TITLE_KEY]: 'repeat(auto-fit, minmax(340px, 1fr))',
};

const isNomenclature = (field: QueryField) => NOMENCLATURE_FIELD_NAMES.includes(field.name);

function SimpleQueryGroup({ fields, rules, onUpdateField }: SimpleQueryGroupProps) {
    const { t } = useLanguage();
    const subcategoryGroups = useMemo(() => groupFieldsBySubcategory(fields), [fields]);

    const renderFieldRow = (field: QueryField) => {
        const pairedName = PAIRED_FIELD_NAME[field.name];
        const pairedField = pairedName ? fields.find(f => f.name === pairedName) : undefined;
        return (
            <FieldRow
                key={field.name}
                field={field}
                rule={rules.find(r => r.field === field.name)}
                onUpdateField={onUpdateField}
                pairedField={pairedField}
                pairedRule={pairedField && rules.find(r => r.field === pairedField.name)}
            />
        );
    };

    const renderFieldRows = (titleKey: string, groupFields: QueryField[]) => {
        const visible = groupFields.filter(field => !MERGED_INTO_FIELD_NAMES.has(field.name));
        const nomenclature = visible.filter(isNomenclature);
        const others = visible.filter(field => !isNomenclature(field));

        if (titleKey !== PURCHASE_BEHAVIOR_CATEGORY_TITLE_KEY || nomenclature.length % 2 === 0 || others.length === 0) {
            return visible.map(renderFieldRow);
        }
        return (
            <>
                {nomenclature.map(renderFieldRow)}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {others.map(renderFieldRow)}
                </Box>
            </>
        );
    };

    return (
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 2 }}>
                {t('pages.clients.queryBuilder.simple.searchCriteria')}
            </Typography>

            {fields.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', bgcolor: 'background.paper', borderRadius: 1, border: '2px dashed', borderColor: 'divider' }}>
                    <Typography variant="body2">
                        {t('pages.clients.queryBuilder.simple.noFieldsAvailable')}
                    </Typography>
                </Box>
            ) : (
                <>
                    {subcategoryGroups.map(({ titleKey, fields: groupFields }, index) => (
                        <Box key={titleKey} sx={{ mb: index < subcategoryGroups.length - 1 ? 3 : 0 }}>
                            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700, mb: 0.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                                {t(titleKey)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                                {t(toHelpKey(titleKey))}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: GRID_COLUMNS_BY_CATEGORY[titleKey] ?? DEFAULT_GRID_COLUMNS,
                                    alignItems: 'start',
                                    gap: 1.5,
                                }}
                            >
                                {renderFieldRows(titleKey, groupFields)}
                            </Box>
                        </Box>
                    ))}
                    <SelectionSummary fields={fields} rules={rules} />
                </>
            )}
        </Paper>
    );
}

export default SimpleQueryGroup;
